import classnames from 'classnames';
import { values } from 'lodash';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { translateKey } from '../../translate';
import { Feedback } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

interface IEnumDropdownInputProps<E> {
    className?: string;
    disabled?: boolean;
    enumName?: string;
    error?: string;
    excludeFromEnum?: E[keyof E][];
    jsEnum: E;
    newstyles?: boolean;
    noSelectPlaceHolder?: boolean;
    onChange: (value: E[keyof E] | null) => Promise<void> | void;
    onDoValidate?: (value: E[keyof E] | null) => Promise<void> | void;
    placeholder?: string;
    sortDirection?: 'ASC' | 'DESC';
    value: E[keyof E] | null;
}

export function EnumDropdownInput<E>(props: FormContextProps<IEnumDropdownInputProps<E>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => {
                const noSelection = !properties.value;
                const selectClassName = classnames(
                    'form-control form-dropdown',
                    props.newstyles && 'form-dropdown-ds',
                    { 'form-control-danger': !!properties.error, pristine: noSelection },
                    properties.className
                );

                return (
                    <>
                        <select
                            className={selectClassName}
                            disabled={properties.disabled || false}
                            key="select"
                            onChange={async (e) => {
                                const value = e.target.value === '' ? null : (e.target.value as E[keyof E] & string);
                                await properties.onChange(value);
                                if (properties.onDoValidate) {
                                    await properties.onDoValidate(value);
                                }
                            }}
                            value={(properties.value || '') as string}
                        >
                            {!properties.noSelectPlaceHolder && (
                                <option
                                    key={-1}
                                    value=""
                                >
                                    {properties.placeholder ? properties.placeholder : translateKey('common.select')}
                                </option>
                            )}
                            {getEnumValuesInArray().map((value: string[], key) => (
                                <option
                                    key={key}
                                    value={value}
                                >
                                    {properties.enumName ? translateKey(`${properties.enumName}Enum.${value}`) : value}
                                </option>
                            ))}
                        </select>
                        <Feedback
                            error={properties.error}
                            key="feedback"
                        />
                    </>
                );
            }}
        </FormContextConsumer>
    );

    function getEnumValuesInArray() {
        const enumValues = props.sortDirection ? values(getSortedEnumValues(props.jsEnum)) : values(props.jsEnum);

        if (props.excludeFromEnum && props.excludeFromEnum.length > 0) {
            for (const excludeItem of props.excludeFromEnum) {
                enumValues.splice(enumValues.indexOf(excludeItem, 1));
            }
        }

        return enumValues;
    }

    function getSortedEnumValues(enumValues: E) {
        return values(enumValues).sort((a, b) => {
            const valueA = translateKey(`${props.enumName}Enum.${a}`);
            const valueB = translateKey(`${props.enumName}Enum.${b}`);
            if (valueA > valueB) {
                return props.sortDirection === 'ASC' ? 1 : -1;
            } else if (valueA < valueB) {
                return props.sortDirection === 'ASC' ? -1 : 1;
            } else {
                return 0;
            }
        });
    }
}

export function EnumDropdownField<E>(props: FormContextProps<IEnumDropdownInputProps<E> & IFieldProps>) {
    return (
        <Field {...props}>
            <EnumDropdownInput {...props} />
        </Field>
    );
}
